<script setup lang="ts">
import { ref } from "vue";
import { FetchError } from "ofetch";

definePageMeta({
  middleware: ["$guest"],
});

interface RegisterPayload {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
}

interface ValidationError {
  [key: string]: string[];
}

const errors = ref<ValidationError>({});

const form = ref({
  name: "",
  email: "",
  password: "",
  password_confirmation: "",
});

const { refreshUser } = useSanctum();

async function registerUser(payload: RegisterPayload) {
  try {
    await useSanctumFetch("/api/register", {
      method: "POST",
      body: JSON.stringify(payload),
    });

    await refreshUser();

    return navigateTo("/");
  } catch (e) {
    if (e instanceof FetchError && e.response?.status === 422) {
      // 422 validation error
      errors.value = e.response._data.errors;
    }
  }
}
</script>
<template>
  <div
    class="flex flex-col justify-center flex-1 min-h-full px-6 py-12 lg:px-8"
  >
    <div class="sm:mx-auto sm:w-full sm:max-w-sm">
      <div class="flex justify-center">
        <NuxtLink to="/" class="">
          <div class="w-[250px] h-[50px] overflow-hidden">
        <img src="/images/Bali_Xperiences_Logo.png" alt="Logo" class="object-cover w-full h-full" />
      </div>
        </NuxtLink>
      </div>
      <h2
        class="mt-10 text-2xl font-bold leading-9 tracking-tight text-center text-gray-900"
      >
        Register Account
      </h2>
    </div>

    <div class="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
      <form class="space-y-6" @submit.prevent="registerUser(form)">
        <div>
          <label
            for="name"
            class="block text-sm font-medium leading-6 text-gray-900"
            >Name</label
          >
          <div class="mt-2">
            <input
              v-model="form.name"
              id="name"
              name="name"
              type="text"
              autocomplete="name"
              class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
            <span v-if="errors.name" class="text-red-500">{{
              errors.name[0]
            }}</span>
          </div>
        </div>
        <div>
          <label
            for="email"
            class="block text-sm font-medium leading-6 text-gray-900"
            >Email address</label
          >
          <div class="mt-2">
            <input
              v-model="form.email"
              id="email"
              name="email"
              type="email"
              autocomplete="email"
              class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
            <span v-if="errors.email" class="text-red-500">{{
              errors.email[0]
            }}</span>
          </div>
        </div>

        <div>
          <div class="flex items-center justify-between">
            <label
              for="password"
              class="block text-sm font-medium leading-6 text-gray-900"
              >Password</label
            >
          </div>
          <div class="mt-2">
            <input
              v-model="form.password"
              id="password"
              name="password"
              type="password"
              autocomplete="current-password"
              class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
            <span v-if="errors.password" class="text-red-500">{{
              errors.password[0]
            }}</span>
          </div>
        </div>
        <div>
          <div class="flex items-center justify-between">
            <label
              for="password_confirmation"
              class="block text-sm font-medium leading-6 text-gray-900"
              >Password confirmation</label
            >
          </div>
          <div class="mt-2">
            <input
              v-model="form.password_confirmation"
              id="password_confirmation"
              name="password_confirmation"
              type="password"
              autocomplete="current-password"
              class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
            <span v-if="errors.password_confirmation" class="text-red-500">{{
              errors.password_confirmation[0]
            }}</span>
          </div>
        </div>

        <div>
          <button
            type="submit"
            class="flex w-full justify-center rounded-md bg-primary px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-primary-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            Register
          </button>
        </div>
      </form>
      <p class="mt-10 text-center text-gray-400 text-sm/6">
        Already have an account?
        {{ " " }}
        <NuxtLink
          to="/login"
          class="font-semibold text-primary opacity-80 hover:text-indigo-300"
          >Log in here
        </NuxtLink>
      </p>
    </div>
  </div>
</template>
